import { AppConfig } from '@/config';
import { handleLogout, httpServices } from './client';

/** Chat runs (`/llm/chat/runs`). Contract: rakuyaku-app-api/docs/v2/chat-runs-api.md. */

export type RunMode = 'standard' | 'thinking' | 'comparison' | 'chat_only';

export type ServerRunStatus =
  | 'queued'
  | 'running'
  | 'stopping'
  | 'stopped'
  | 'completed'
  | 'failed'
  /** Ended with a clarification (§17): not active, the composer stays usable. */
  | 'awaiting_reply';

export const ACTIVE_RUN_STATUSES: ReadonlySet<string> = new Set([
  'queued',
  'running',
  'stopping',
]);

export type ParentKind =
  | 'retry'
  | 'alternative'
  | 'edit'
  | 'skip_clarification';

/** A run's kind: what a client sends, or `reply`, which only the server sets. */
export type RunKind = ParentKind | 'reply';

export type ComparisonRequest =
  | { purpose: 'changes'; baseline_file_id: number }
  | {
      purpose: 'guidelines';
      document_to_check_id: number;
      reference_file_ids?: number[];
    };

/** POST /llm/chat/runs body (§3.1). */
export interface CreateRunRequest {
  inquiry_id: number;
  query?: string;
  mode?: RunMode;
  system_language?: 'japanese' | 'english';
  parent?: { kind: ParentKind; message_id: number };
  send_ready_only?: boolean;
  comparison?: ComparisonRequest;
  idempotency_key?: string;
  /**
   * When the user pressed Send, on the clock of the drafts'
   * `client_saved_at` (a new question records it as the draft's clear).
   */
  client_sent_at?: string;
}

/** One `citations` item (§5). */
export interface Citation {
  n: number;
  file_id: number | null;
  name: string | null;
  location: string | null;
  excerpt: string | null;
}

export interface RunError {
  code: string;
  message: string;
  retryable: boolean;
}

export interface SourceRecordFile {
  id: number;
  name: string | null;
  source?: string | null;
  size_bytes?: number | null;
}

/** What a run was submitted with (§14 `source_record`). */
export interface SourceRecord {
  origin: 'selection' | 'parent' | 'chat_only' | 'legacy' | string;
  folder_ids?: number[] | null;
  files: SourceRecordFile[] | null;
  duplicates?: unknown[] | null;
  excluded?: { id: number; name: string | null; reason: string }[] | null;
  total_bytes?: number | null;
  send_ready_only?: boolean | null;
}

/** Purpose and roles frozen on a comparison run (§3.5). */
export interface RunComparison {
  purpose: 'changes' | 'guidelines';
  baseline: { id: number; name: string | null } | null;
  targets: { id: number; name: string | null }[];
  document_to_check: { id: number; name: string | null } | null;
  references: { id: number; name: string | null }[];
  defaulted: boolean;
}

/** GET /llm/chat/runs/{id} (§8). */
export interface RunDetails {
  run_id: string;
  message_id: number | null;
  inquiry_id: number;
  /** Who started the run; compared with GET /llm/me/info `id`. */
  user_id?: number | null;
  mode: RunMode | null;
  status: ServerRunStatus;
  stage: string | null;
  parent: { kind: RunKind; message_id: number } | null;
  source_record: SourceRecord | null;
  comparison: RunComparison | null;
  custom_prompt: string | null;
  error: RunError | null;
  started_at: string | null;
  finished_at: string | null;
  duration_ms: number | null;
  created_at: string;
  last_seq: number;
  partial_text: string | null;
}

/** A refusal with a `code` (409 run_active, 422 pre-check or resubmission). */
export interface RunRefusal {
  code?: string;
  message?: string;
  active_run_id?: string;
  reason?: string | null;
  sources?: { id: number; name: string | null; reason: string }[];
  pending?: { id: number; name: string | null; status: string }[];
  kind?: string;
  parent_status?: string;
  /** Laravel validation errors (422 without a code). */
  errors?: Record<string, string[]>;
}

export type CreateRunResult =
  | {
      kind: 'created';
      runId: string;
      messageId: number;
      status: ServerRunStatus;
    }
  | { kind: 'conflict'; activeRunId: string | null; body: RunRefusal }
  | { kind: 'rejected'; status: number; body: RunRefusal }
  /** No answer from the server (network error). Safe to resend with the same key. */
  | { kind: 'network' }
  | { kind: 'error'; status: number; body: RunRefusal };

interface RawResponse<T> {
  status: number;
  data: T | undefined;
}

/**
 * A request that keeps the status and body of every response (the shared
 * HttpService turns errors into `{success: false}` and drops the body).
 * Status 0 means no response arrived.
 */
/**
 * These calls answer at once (the run itself works in the background); a
 * dead connection must not hold them for the client's default 10 minutes.
 */
const RUN_REQUEST_TIMEOUT_MS = 20_000;

const request = async <T>(
  method: 'get' | 'post',
  url: string,
  data?: unknown,
): Promise<RawResponse<T>> => {
  try {
    const response = await httpServices.llmService.axiosInstance.request<T>({
      method,
      url,
      data,
      timeout: RUN_REQUEST_TIMEOUT_MS,
      validateStatus: () => true,
    });
    if (response.status === 401) handleLogout('expired');
    return { status: response.status, data: response.data };
  } catch {
    return { status: 0, data: undefined };
  }
};

/** POST /llm/chat/runs (§3). */
export const createRun = async (
  body: CreateRunRequest,
): Promise<CreateRunResult> => {
  const response = await request<
    { run_id: string; message_id: number; status: ServerRunStatus } & RunRefusal
  >('post', '/chat/runs', body);
  const data = response.data ?? {};
  if (response.status === 0) return { kind: 'network' };
  const created = data as {
    run_id?: string;
    message_id?: number;
    status?: ServerRunStatus;
  };
  if ((response.status === 202 || response.status === 200) && created.run_id) {
    return {
      kind: 'created',
      runId: created.run_id,
      messageId: Number(created.message_id),
      status: created.status ?? 'queued',
    };
  }
  if (response.status === 409) {
    return {
      kind: 'conflict',
      activeRunId: (data as RunRefusal).active_run_id ?? null,
      body: data as RunRefusal,
    };
  }
  if (response.status === 422) {
    return { kind: 'rejected', status: 422, body: data as RunRefusal };
  }
  return { kind: 'error', status: response.status, body: data as RunRefusal };
};

/** GET /llm/chat/runs/{id} (§8); null when the run is unknown or unreadable. */
export const getRun = async (
  runId: string,
): Promise<{ status: number; run: RunDetails | null }> => {
  const response = await request<RunDetails>('get', `/chat/runs/${runId}`);
  const ok = response.status === 200 && !!response.data?.run_id;
  return { status: response.status, run: ok ? response.data! : null };
};

export interface CancelRunResponse {
  /** `stopped`, `stop_requested`, or the final status when it had finished. */
  status?: string;
  acknowledged?: boolean;
  /** Set when the request failed (no answer, or not 200). */
  failed?: boolean;
}

/** POST /llm/chat/runs/{id}/cancel (§7). */
export const cancelRun = async (runId: string): Promise<CancelRunResponse> => {
  const response = await request<CancelRunResponse>(
    'post',
    `/chat/runs/${runId}/cancel`,
  );
  if (response.status !== 200 || !response.data) return { failed: true };
  return response.data;
};

/** The URL of a run's event stream (§4). */
export const runEventsUrl = (runId: string) =>
  `${AppConfig.axios.llm_stream_endpoint.replace(/\/+$/, '')}/chat/runs/${runId}/events`;

/** The Authorization header for the event stream (same as every /llm call). */
export const streamAuthorization = () =>
  httpServices.llmService.authorization();

/**
 * The active run of an inquiry, from the newest page of its message list
 * (each message carries `run_id` and `run_status`, chat-runs-api.md §14).
 */
export const findActiveRunId = async (
  inquiryId: string,
): Promise<string | null> => {
  const response = await httpServices.llmService.get<{
    success?: boolean;
    data?: { run_id?: string | null; run_status?: string | null }[];
  }>(`/chat/message/${inquiryId}`, { cursor: '' });
  const active = response?.data?.find(
    message =>
      message.run_id && ACTIVE_RUN_STATUSES.has(String(message.run_status)),
  );
  return active?.run_id ?? null;
};
